import React, { Suspense, lazy } from 'react';
import { Hero } from './components/Hero';

/**
 * WOODWORKING FURNITURE PLANS™ sales page.
 * HERO → SNAPSHOT → P04 PREVIEW → PLAN → TECH PROOF → 6 VOLUMES →
 * OFFER → FAQ → FINAL CTA → FOOTER (+ mobile sticky bar after #offer).
 *
 * Everything below the hero loads in separate chunks so the hero (mobile LCP)
 * ships with minimal JS. Each block reserves height while loading.
 */
const named = <K extends string,>(loader: () => Promise<Record<K, React.ComponentType>>, key: K) => lazy(() => loader().then(m => ({
  default: m[key]
})));
const ValueStrip = named(() => import('./components/sections/ValueStrip'), 'ValueStrip');
const ProjectPreviewSection = named(() => import('./components/sections/ProjectPreviewSection'), 'ProjectPreviewSection');
const PlanSection = named(() => import('./components/sections/PlanSection'), 'PlanSection');
const TechProofSection = named(() => import('./components/sections/TechProofSection'), 'TechProofSection');
const CollectionSection = named(() => import('./components/sections/CollectionSection'), 'CollectionSection');
const OfferSection = named(() => import('./components/sections/OfferSection'), 'OfferSection');
const FaqSection = named(() => import('./components/sections/FaqSection'), 'FaqSection');
const FinalCta = named(() => import('./components/sections/FinalCta'), 'FinalCta');
const Footer = named(() => import('./components/Footer'), 'Footer');
const StickyBuyBar = named(() => import('./components/StickyBuyBar'), 'StickyBuyBar');
interface DeferredProps {
  children: React.ReactNode;
  minHeight: string;
  background?: string;
  id?: string;
}

/** The fallback carries the section id so anchors resolve even before the chunk loads. */
function Deferred({
  children,
  minHeight,
  background = 'bg-charcoal',
  id
}: DeferredProps) {
  return <Suspense fallback={<div id={id} className={`w-full ${background}`} style={{
    minHeight
  }} aria-hidden data-id="element-1" data-mp="nv0x5qhcgk43" />} data-id="element-0" data-mp="nuhiqjqwkdut">
      {children}
    </Suspense>;
}
export function App() {
  return <main className="w-full overflow-x-clip bg-charcoal text-white" data-id="element-2" data-mp="35ozsypftiwt">
      <Hero data-id="element-3" data-mp="3uct25gan0gyw" />

      <Deferred minHeight="64px" background="bg-bottle" data-id="element-4" data-mp="3udawg7dqol46">
        <ValueStrip data-id="element-5" data-mp="3ucj8niqdvrog" />
      </Deferred>
      <Deferred minHeight="900px" background="bg-cream" id="project-preview" data-id="element-6" data-mp="d4e94339pv1g">
        <ProjectPreviewSection data-id="element-7" data-mp="d2bf77yzvhrg" />
      </Deferred>
      <Deferred minHeight="420px" background="bg-dune" data-id="element-8" data-mp="86mc4unrniqe">
        <PlanSection data-id="element-9" data-mp="3glea6jyr55fp" />
      </Deferred>
      <Deferred minHeight="900px" background="bg-cream" id="technical-proof" data-id="element-10" data-mp="3gmfvdh9hf8b5">
        <TechProofSection data-id="element-11" data-mp="3bmtgl3e09k08" />
      </Deferred>
      <Deferred minHeight="1000px" background="bg-dune" id="volumes" data-id="element-12" data-mp="3bo4lx7n3mztg">
        <CollectionSection data-id="element-13" data-mp="3qc966g1svjec" />
      </Deferred>
      <Deferred minHeight="900px" id="offer" data-id="element-14" data-mp="3qdafog363xty">
        <OfferSection data-id="element-15" data-mp="3lk7k62etyeb4" />
      </Deferred>
      <Deferred minHeight="620px" background="bg-ivory" id="faq" data-id="element-16" data-mp="3lj67xum2vlrt">
        <FaqSection data-id="element-17" data-mp="2xtv8cgggrxf9" />
      </Deferred>
      <Deferred minHeight="420px" data-id="element-18" data-mp="2xssebznuhev4">
        <FinalCta data-id="element-19" data-mp="2swbs5b07hako" />
      </Deferred>
      <Deferred minHeight="220px" background="bg-charcoal-deep" data-id="element-20" data-mp="2sv0m14jafqqe">
        <Footer data-id="element-21" data-mp="18d9d06h7bks6" />
      </Deferred>

      <Suspense fallback={null} data-id="element-22" data-mp="18c6ou6b9jwfy">
        <StickyBuyBar data-id="element-23" data-mp="18by986nrad12" />
      </Suspense>
    </main>;
}